/**
 * Shared avatar driver — maps a compact networked/AI state onto a character
 * Avatar's pose each frame (weapon/jet/aim/melee/combat/block + speed-inferred
 * locomotion + distance LOD). Used by BOTH remote players (net.ts RemoteShip)
 * and humanoid enemies (world/drones.ts), so there is exactly one place that
 * turns "where/how it's moving + what it's doing" into animation.
 *
 * The caller positions/orients the avatar group BEFORE calling (LOD reads the
 * already-placed world position); this only drives state + the gait update.
 */
import * as THREE from "three";
import { ITEMS, resolveCharacterPose } from "@hyperlight/shared";
import type { Avatar } from "./avatar";

// On-foot avatar LOD (metres from camera). Near: full per-frame gait. Mid:
// gait throttled to every 3rd frame. Far: cheap impostor + frozen pose.
export const AVATAR_LOD_MID = 120;
export const AVATAR_LOD_FAR = 420;

export interface AvatarLodPolicy {
  /** Animation cadence threshold; visual detail is controlled separately. */
  midDistance: number;
  farDistance: number;
  /** Animation stride beyond midDistance (default: 3). */
  midStride?: number;
  /** Optional second animation tier beyond this distance. */
  distantDistance?: number;
  distantStride?: number;
}

export interface AvatarDriveClock { elapsed: number }

const DEFAULT_LOD_POLICY: AvatarLodPolicy = {
  midDistance: AVATAR_LOD_MID,
  farDistance: AVATAR_LOD_FAR,
};

export interface AvatarDriveState {
  /** Held weapon item id (null = unarmed fists / combat stance). */
  weaponId: string | null;
  /** Equipped-but-holstered weapon rendered on the BACK (PlayerState.sw). */
  stowedId?: string | null;
  jet: "none" | "hover" | "glide" | "dive";
  flightPower?:number;
  flightLaunchRemaining?:number;
  divePreparation?:number;
  flightApproach?:number;
  wallRunState?:number;
  wallRunTime?:number;
  ledgeClimbState?:number;
  ledgeClimbTime?:number;
  ledgeAnchor?:Readonly<{x:number;y:number;z:number}>;
  /** Aiming (gun ADS) or in a fighting stance (unarmed). */
  aiming: boolean;
  airborne: boolean;
  crouch: boolean;
  blocking: boolean;
  /** Body-local move direction + climb (drives directional/strafe clips). */
  moveFwd: number;
  moveRight: number;
  climb: number;
  /** Optional measured body-frame velocity (m/s); +right and +forward. */
  lateralSpeed?: number;
  forwardSpeed?: number;
  /** Aim pitch (radians). */
  pitch: number;
  /** Ground speed (m/s) for the gait. */
  groundSpeed: number;
  /** Submersion 0..1 (~1 ⇒ swimming) — selects the swim pose. Default 0. */
  submerge?: number;
  /** LAUNCHED/JUGGLED posture (streamed `lf`): the LiftAir/KipUp full-body override.
   *  Absent/"" = normal. */
  lift?: "" | "up" | "fall" | "down" | "rise";
  /** STAGGERED posture (streamed `st` — a parried attacker): st_loop/st_end override. */
  stagger?: "" | "loop" | "end";
  /** NPC ACTIVITY EMOTE (looping UAL clip key; ""/absent = none) — carries the
   *  stand instead of the neutral idle (counter service, farm work, …). */
  emote?: string;
  /** Explicit NPC fighting behavior; empty-handed player readiness is an alert pose. */
  combat?: boolean;
}

/**
 * Drive `avatar` from `st`. `lodFrame` is a monotonically increasing frame
 * counter (for the mid-LOD gait throttle). Returns nothing.
 */
export function driveAvatar(
  avatar: Avatar,
  st: AvatarDriveState,
  dt: number,
  camPos: THREE.Vector3,
  lodFrame: number,
  lod: AvatarLodPolicy = DEFAULT_LOD_POLICY,
  lodPhase = 0,
  clock?: AvatarDriveClock,
): "near" | "mid" | "far" {
  const wdef = st.weaponId ? ITEMS[st.weaponId] : undefined;
  // The SHIELD is a stance, not a prop: it manifests nothing and reads as unarmed —
  // remotes must render it exactly the way the owner does.
  const shielded = wdef?.weapon?.construct === "shield";
  avatar.setWeapon(shielded ? null : st.weaponId);
  avatar.setStowedWeapon(st.stowedId ?? null);
  avatar.setJet(st.jet);
  avatar.setFlightPower(st.flightPower??0);
  avatar.setFlightLaunchRemaining(st.flightLaunchRemaining??0);
  avatar.setDivePreparation(st.divePreparation??0);
  avatar.setFlightApproach(st.flightApproach??0);
  avatar.setWallRun(st.wallRunState??0,st.wallRunTime??0);
  avatar.setLedgeClimb(st.ledgeClimbState??0,st.ledgeClimbTime??0,st.ledgeAnchor);
  avatar.setLift(st.lift ?? ""); // launched-body override (lift_*/kip_up)
  avatar.setStagger(st.stagger ?? ""); // parried-body override (st_loop/st_end)
  avatar.setEmote(st.emote ?? ""); // NPC activity emote (idle replacement)
  const isGun = !shielded && !!wdef?.weapon && wdef.weapon.class !== "melee";
  const isMeleeWeapon = !!wdef?.weapon && wdef.weapon.class === "melee";
  const fists = shielded || !wdef?.weapon;
  const pose = resolveCharacterPose({ itemId: st.weaponId, speed: st.groundSpeed, crouching: st.crouch,
    grounded: !st.airborne, aimHeld: st.aiming, blocked: st.jet === "glide" || st.jet === "dive" });
  avatar.setAim(pose.aiming && isGun, st.pitch);
  // Remote blades render the SAME sd_ strafe stance the owner sees (setSwordFocus) — the
  // legacy procedural boxing-guard (setMelee) was deleted 2026-07-23 with the dead unarmed
  // system; locals had it off since the sd stance shipped.
  avatar.setSwordFocus(st.aiming && isMeleeWeapon);
  // A raised SHIELD is not the brawler's fighting stance, even though it borrows the brawler's
  // BLOCK: the owner renders it as the dh_block cover held over the 8-way walk (walkMode), with
  // no DHTH stance and no fist glow. Mirror that exactly — the same construct must not read one
  // way on its owner's screen and another way on everyone else's. `bl` is only set while the
  // guard is actually up (energy left), so it alone decides the cover.
  const guarding = shielded && st.blocking;
  const brawling = st.combat === true && st.aiming && fists && !shielded;
  avatar.setCombat(brawling);
  avatar.setWalkMode(guarding || (!brawling && fists && pose.stance === "readied"));
  avatar.setBlocking(guarding || (brawling && st.blocking));

  // Distance LOD: far → frozen impostor; mid → throttled gait; near → every frame.
  const lodDist = avatar.group.position.distanceTo(camPos);
  if (lodDist > lod.farDistance) {
    if (clock) clock.elapsed = 0;
    avatar.setFarLOD(true);
    return "far";
  }
  avatar.setFarLOD(false);
  const stride = lod.distantDistance !== undefined && lodDist > lod.distantDistance
    ? lod.distantStride ?? lod.midStride ?? 3
    : lodDist > lod.midDistance ? lod.midStride ?? 3 : 1;
  if (clock) clock.elapsed += dt;
  if ((lodFrame + lodPhase) % stride === 0) {
    const updateDt = clock ? clock.elapsed : dt * stride;
    if (clock) clock.elapsed = 0;
    avatar.update(
      updateDt,
      st.groundSpeed,
      !st.airborne,
      // Sprint clip only above the run tier — the player's RUN is 5.5, SPRINT
      // 7.0, so split between them (a steady RUN must not read as a sprint).
      st.groundSpeed > 6.25,
      { fwd: st.moveFwd, right: st.moveRight, climb: st.climb, lateralSpeed: st.lateralSpeed, forwardSpeed: st.forwardSpeed },
      st.crouch,
      (st.submerge ?? 0) > 0.9, // ~1 ⇒ swimming (wading caps ≈0.875) → swim clips
    );
  }
  return stride > 1 ? "mid" : "near";
}
